<script lang="ts">
	import { resolve } from '$app/paths';
	import ArrowIcon from '$lib/components/marketing/redesign2025/ArrowIcon.svelte';
	import PageHero from '$lib/components/marketing/redesign2025/PageHero.svelte';
	import { cn } from '$lib/utils';
	import { m } from '$lib/paraglide/messages.js';
	import { getLocale, localizeHref } from '@/paraglide/runtime';

	const locale = $derived(getLocale());

	const options = $derived.by(() => {
		void locale;
		return [
			{
				amount: 25,
				title: m.gift_option_25_title(),
				description: m.gift_option_25_desc(),
				image: '/img/gifts/Zarpar-regalo-25.avif',
				imageClass: 'object-contain p-4'
			},
			{
				amount: 50,
				title: m.gift_option_50_title(),
				description: m.gift_option_50_desc(),
				image: '/img/gifts/zarpar-regalo-50.webp',
				imageClass: 'object-cover'
			},
			{
				amount: 100,
				title: m.gift_option_100_title(),
				description: m.gift_option_100_desc(),
				image: '/img/gifts/Zarpar-regalo-100.avif',
				imageClass: 'object-cover'
			},
			{
				amount: 200,
				title: m.gift_option_200_title(),
				description: m.gift_option_200_desc(),
				image: '/img/gifts/Zarpar-regalo-200.webp',
				imageClass: 'object-cover'
			},
			{
				amount: 500,
				title: m.gift_option_500_title(),
				description: m.gift_option_500_desc(),
				image: '/img/gifts/Zarpar-regalo-500.webp',
				imageClass: 'object-cover p-3'
			}
		] as const;
	});

	const topOptions = $derived(options.slice(0, 3));
	const bottomOptions = $derived(options.slice(3));
</script>

<PageHero
	eyebrow={m.gift_hero_eyebrow()}
	breadcrumbLabel={m.gift_hero_breadcrumb()}
	lead={m.gift_hero_lead()}
>
	{#snippet heroImage(className)}
		<enhanced:img
			src="/static/img/landing/redesign-2025/photos/sunset-cockpit.avif"
			alt=""
			class={className}
			fetchpriority="high"
			sizes="100vw"
		></enhanced:img>
	{/snippet}
	{m.gift_hero_h1_before()}<em class="italic">{m.gift_hero_h1_em()}</em>
</PageHero>

<section class="py-20 md:py-[100px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="grid items-start gap-9 lg:grid-cols-[0.9fr_1.1fr] lg:gap-[70px]">
			<div>
				<span
					class="mb-5 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-muted uppercase"
					>{m.gift_intro_eyebrow()}</span
				>
				<h2 class="text-[clamp(1.9rem,3.4vw,3rem)] leading-[1.06]">
					{m.gift_intro_h2()}
				</h2>
			</div>
			<div>
				<p class="mb-5 text-[1.1rem] text-zp-prose">
					{m.gift_intro_p1()}
				</p>
				<p class="mb-0 text-[1.1rem] text-zp-prose">
					{m.gift_intro_p2_before()}<strong class="text-zp-ink">{m.gift_intro_p2_strong()}</strong
					>{m.gift_intro_p2_after()}
				</p>
			</div>
		</div>
	</div>
</section>

<section class="bg-zp-paper2 py-20 md:py-[100px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="mb-14 grid items-start gap-9 lg:grid-cols-[0.9fr_1.1fr] lg:gap-[70px]">
			<div>
				<span
					class="mb-5 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-muted uppercase"
					>{m.gift_grid_eyebrow()}</span
				>
				<h2 class="text-[clamp(1.9rem,3.4vw,3rem)] leading-[1.06]">
					{m.gift_grid_h2()}
				</h2>
			</div>
			<p class="m-0 text-[1.1rem] text-zp-prose">
				{m.gift_grid_p()}
			</p>
		</div>

		<div class="grid items-stretch gap-7 sm:grid-cols-2 lg:grid-cols-3">
			{#each topOptions as option (option.amount)}
				<div
					class="flex h-full flex-col overflow-hidden rounded-2xl border border-zp-line bg-white"
				>
					<div class="aspect-4/3 shrink-0 overflow-hidden bg-zp-paper2">
						<img
							src={option.image}
							alt={option.title}
							class={cn('h-full w-full', option.imageClass)}
						/>
					</div>
					<div class="flex flex-1 flex-col px-6 py-7 md:px-8 md:py-[34px]">
						<p class="font-serif text-[1.05rem] text-zp-accent">{option.amount} €</p>
						<h3 class="mb-2 text-[1.3rem] leading-snug">{option.title}</h3>
						<p class="mb-0 flex-1 text-[0.98rem] text-zp-muted italic">
							"{option.description}"
						</p>
						<a
							href={resolve(localizeHref(`/regala-zarpar/tarjeta-regalo?amount=${option.amount}`))}
							class="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md border border-transparent bg-zp-navy px-6 py-3.5 text-[0.86rem] font-semibold text-zp-paper transition-all hover:-translate-y-px hover:bg-zp-ink md:mt-auto"
						>
							{m.gift_regalar_btn({ amount: option.amount })}
							<ArrowIcon />
						</a>
					</div>
				</div>
			{/each}
		</div>

		<div class="mx-auto mt-7 grid max-w-[820px] items-stretch gap-7 sm:grid-cols-2">
			{#each bottomOptions as option (option.amount)}
				<div
					class="flex h-full flex-col overflow-hidden rounded-2xl border border-zp-line bg-white"
				>
					<div class="aspect-4/3 shrink-0 overflow-hidden bg-zp-paper2">
						<img
							src={option.image}
							alt={option.title}
							class={cn('h-full w-full', option.imageClass)}
						/>
					</div>
					<div class="flex flex-1 flex-col px-6 py-7 md:px-8 md:py-[34px]">
						<p class="font-serif text-[1.05rem] text-zp-accent">{option.amount} €</p>
						<h3 class="mb-2 text-[1.3rem] leading-snug">{option.title}</h3>
						<p class="mb-0 flex-1 text-[0.98rem] text-zp-muted italic">
							"{option.description}"
						</p>
						<a
							href={resolve(localizeHref(`/regala-zarpar/tarjeta-regalo?amount=${option.amount}`))}
							class="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md border border-transparent bg-zp-navy px-6 py-3.5 text-[0.86rem] font-semibold text-zp-paper transition-all hover:-translate-y-px hover:bg-zp-ink md:mt-auto"
						>
							{m.gift_regalar_btn({ amount: option.amount })}
							<ArrowIcon />
						</a>
					</div>
				</div>
			{/each}
		</div>
	</div>
</section>

<section
	class="zp-cta-band"
	style="background-image: url('/img/landing/redesign-2025/photos/cies-islands.avif')"
>
	<div class="relative z-2 mx-auto max-w-[1240px] px-5 py-24 text-zp-paper md:px-10 md:py-[120px]">
		<h2 class="mb-5 max-w-[16ch] text-[clamp(2rem,4vw,3.4rem)] leading-[1.05] text-white">
			{m.gift_cta_h2()}
		</h2>
		<p class="mb-8 max-w-[46ch] text-[1.12rem] text-zp-paper/85">
			{m.gift_cta_p()}
		</p>
		<a
			href={resolve(localizeHref('/barcos'))}
			class="inline-flex items-center gap-2 rounded-[40px] border border-transparent bg-zp-paper px-6 py-3.5 text-[0.86rem] font-semibold text-zp-navy transition-all hover:-translate-y-px hover:bg-white"
		>
			{m.gift_cta_btn()}
			<ArrowIcon />
		</a>
	</div>
</section>
